/**
 * Returns a human-readable relative time string for a given date.
 * e.g. "just now", "5 minutes ago", "3 days ago"
 *
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Relative time string
 */
export function formatRelativeTime(dateInput) {
    // Guard falsy input before constructing a Date: new Date(null) coerces to
    // the Unix epoch (a valid date), which would otherwise render as a bogus
    // "56 years ago" instead of an empty string. Mirrors dateHelpers' falsy
    // guard so a missing timestamp is treated as "no date" consistently.
    if (!dateInput) return '';
    const date = new Date(dateInput);
    if (isNaN(date)) return '';
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 0) return 'just now';
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMinutes < 1) return 'just now';
    if (diffMinutes === 1) return '1 minute ago';
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffWeeks === 1) return '1 week ago';
    if (diffMonths < 1) return `${diffWeeks} weeks ago`;
    if (diffMonths === 1) return '1 month ago';
    if (diffYears < 1) return `${diffMonths} months ago`;
    if (diffYears === 1) return '1 year ago';
    return `${diffYears} years ago`;
}

/**
 * Returns a full, human-readable absolute date string for tooltip display.
 * e.g. "January 16, 2026 at 3:42 PM"
 *
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Full absolute date string
 */
export function formatFullDate(dateInput) {
    // Guard falsy input before constructing a Date: new Date(null) coerces to
    // the Unix epoch (a valid date), which would otherwise render as a bogus
    // "January 1, 1970" tooltip instead of an empty string. Mirrors
    // dateHelpers' falsy guard so a missing timestamp is treated as "no date".
    if (!dateInput) return '';
    const date = new Date(dateInput);
    if (isNaN(date)) return '';
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}
